import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentAuthorizationService,
  AppointmentModel,
  AuthorizationLikeItem,
  AuthorizationRule,
  AuthorizationRuleAPI,
  AuthorizationRulesService,
  AvailityServiceReview,
  ClinicService,
  CrudService,
  FacilityService,
  filterAction,
  findInsuranceForAccount,
  INSURANCE_PROVIDER_TRANSACTION_TYPE,
  InsuranceProviderConfiguration,
  InsuranceProviderLookupsService,
  npiValidator,
  Patient,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceType,
  PatientRelationshipToSubscriber,
  ProductModel,
  ReferralModel,
  ReferrerModel,
  ReferrersService,
  SelectOption,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { filter as _filter, flatten, uniq } from 'lodash'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  type Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs'

import type { ServiceReviewInquiryFormValue } from '../service-review-inquiry-form'

export interface ServiceReviewInquiryDialogData {
  patient: Patient
  type: 'referrals' | 'authorizations'
  appointment: AppointmentModel
  insurance?: PatientInsurance
  products?: ProductModel[]
}

export interface PullPriorAuthorizationPayload {
  requestTypeCode: InsuranceProviderConfiguration.SUBTYPE_ID
  fromDate: string
  toDate: string
  certificationIssueDate: string
  certificationNumber: string
  referenceNumber: string
  authRuleIds?: string[]
  relationship?: PatientRelationshipToSubscriber
  subscriberFirstName?: string
  subscriberLastName?: string
}

export interface PullPriorAuthorizationResponse {
  action: string
  data: AvailityServiceReview.Review[]
  message: string
  topic: string
}

export type PullPriorAuthorizationMode = 'request' | 'results'

@Component({
  templateUrl: './pull-prior-authorization.component.html',
  styleUrls: ['./pull-prior-authorization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceReviewInquiryDialogComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>()
  public isLoading$ = new BehaviorSubject<boolean>(false)
  public noSelection = false

  public readonly SUBTYPE = InsuranceProviderConfiguration.SUBTYPE_ID
  protected readonly authPullUnsupportedText = ' (278I Unsupported)'

  public loading$ = combineLatest([
    this.isLoading$,
    this.insuranceProviderLookupsService.loading$,
  ]).pipe(
    map(([isLoading, isLookupsLoading]) => isLoading || isLookupsLoading),
  )

  public mode$ = new BehaviorSubject<PullPriorAuthorizationMode>('request')

  public availableServiceTypes: InsuranceProviderConfiguration.SUBTYPE_ID[]
  public availableInsurances$: Observable<SelectOption<PatientInsurance>[]> = this.patientDataService.insurancePairs$.pipe(
    takeUntil(this.onDestroy$),
    map((pairs) => pairs.map(([policy, lookup]) => {
      const ruleForPolicy = this.data.appointment.authRules?.find((rule) => rule.payerGroupIds?.some((id) => id === lookup.payerGroupId))
      let canPull: boolean
      if (ruleForPolicy) {
        if (ruleForPolicy.systemPortal?.api === AuthorizationRuleAPI.AVAILITY) {
          canPull = lookup.supports(INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY)
        } else {
          canPull = !!ruleForPolicy.systemPortal?.api
        }
      } else {
        canPull = lookup.supports(INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY)
      }

      let viewValue = policy.memberId ? `${lookup.viewValue} (${policy.memberId})` : lookup.viewValue

      if (!canPull) {
        viewValue += this.authPullUnsupportedText
      }

      const option: SelectOption<PatientInsurance> = {
        value: policy,
        viewValue,
        disabled: !canPull,
      }
      return option
    })),
  )

  public formGroup = this.fb.group({
    insurance: this.fb.control<PatientInsurance>(null, Validators.required),
    relationship: this.fb.control<PatientRelationshipToSubscriber>(null),
    subscriberFirstName: this.fb.control<string>(null),
    subscriberLastName: this.fb.control<string>(null),
    facility: this.fb.control<string>(null, Validators.required),
    orderingPhysicianNPI: this.fb.control<string>(null, [Validators.required, npiValidator]),
    requestTypeCode: this.fb.control<InsuranceProviderConfiguration.SUBTYPE_ID>(null, Validators.required),
    authRules: this.fb.control<AuthorizationRule[]>(null, Validators.required),
    priorAuthorization: this.fb.control(null),
    serviceReviews: this.fb.control<AuthorizationLikeItem[]>(null),
  })

  public errorsVisible$ = new BehaviorSubject<boolean>(false)

  public authRules$: Observable<SelectOption<AuthorizationRule>[]> = this.authorizationRulesService.collectionSuccess$
    .pipe(
      combineLatestWith(this.patientDataService.insurancePairs$),
      map(([authRules, insurancePairs]) => {
        const insuranceLookups = insurancePairs.map(([, lookup]) => lookup)

        const options = authRules
          .filter((rule, i, self) => self.findIndex(r => r.id === rule.id) === i) // unique by id
          .filter(rule => rule.systemPortal.isAuthIdentificationSupported)
          .map((rule): SelectOption<AuthorizationRule> => {
            const insuranceLookup = insuranceLookups.find((lookup) => rule.payerGroups.find((l) => l.id === lookup.payerGroupId))
            let name = rule.systemPortal ? rule.systemPortal.name : 'Unknown Portal'

            if (!insuranceLookup) {
              return {
                value: rule,
                viewValue: `${name} (No Insurance Lookup)`,
                disabled: true,
              }
            }

            const isAuthPullSupported = rule.systemPortal?.api === AuthorizationRuleAPI.AVAILITY
              ? insuranceLookup.supports(INSURANCE_PROVIDER_TRANSACTION_TYPE.SERVICE_REVIEWS_INQUIRY)
              : true

            if (!isAuthPullSupported) {
              name += this.authPullUnsupportedText
            }

            const authRulesControl = this.formGroup.get('authRules')
            authRulesControl.patchValue(
              (authRulesControl.value || []).filter((r: AuthorizationRule) => r.id !== rule.id),
            )

            return {
              value: rule,
              viewValue: name,
              disabled: !isAuthPullSupported,
            }
          })

        // Show each portal only once in options, preferring enabled options when duplicates exist.
        return options.reduce((uniqueOptions, option) => {
          const existingIndex = uniqueOptions.findIndex((currentOption) =>
            this.getPortalKey(currentOption.value) === this.getPortalKey(option.value),
          )

          if (existingIndex === -1) {
            uniqueOptions.push(option)
            return uniqueOptions
          }

          if (uniqueOptions[existingIndex].disabled && !option.disabled) {
            uniqueOptions[existingIndex] = option
          }

          return uniqueOptions
        }, [] as SelectOption<AuthorizationRule>[])
      }),
      tap((authRules) => {
        const enabledRules = authRules.filter(r => !r.disabled)
        const appointmentAuthRules = this.data.appointment.authRules || []
        let selectedAuthRules = enabledRules.filter((r) => appointmentAuthRules.some((pr) => pr.id === r.value.id)).map(r => r.value)

        // make sure at least one auth rule is selected
        if (enabledRules.length > 0 && selectedAuthRules.length === 0) {
          selectedAuthRules = enabledRules.map(r => r.value)
        }

        this.formGroup.get('authRules').setValue(selectedAuthRules)
      }),
    )

  public serviceReviews$: Observable<AuthorizationLikeItem[]> = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      filter((results) => ['success', 'partial'].includes(results.status)),
      map((results) => {
        const serviceReviewsByAuthRule = (results.data || []).filter((row: Record<string, any>) => row.success)
        this.isLoading$.next(false)
        if (!serviceReviewsByAuthRule?.length) {
          this.mode$.next('request')
          return []
        }

        this.mode$.next('results')

        this.formGroup.controls.serviceReviews.addValidators([
          Validators.required,
          Validators.minLength(1),
        ])

        // Each row is one auth rule's pull and carries the id of the ApiRequestResponse row it
        // wrote. Flattening loses that pairing, so stamp it on the reviews first - the API
        // links the authorizations it creates back to the call that found them, and without
        // the id a manual pull shows in Portal Transactions with no result columns. Optum
        // resolves each case with a separate call, so a review that already names its own row
        // keeps it.
        return serviceReviewsByAuthRule.reduce((acc: AuthorizationLikeItem[], row: Record<string, any>) => {
          return [
            ...acc,
            ...row.serviceReviews.map((serviceReview: AuthorizationLikeItem) => ({
              ...serviceReview,
              apiRequestResponseId: serviceReview.apiRequestResponseId ?? row.apiRequestResponseId,
            })),
          ]
        }, [])
      }),
      shareReplay(1),
    )

  public serviceReviewErrors$ = this.websocketService.clinicianMessage$
    .pipe(
      filterAction(WebsocketAction.PRIOR_AUTHORIZATION_INDEX),
      filter((results) => results.status !== 'success'),
      catchError(() => {
        this.isLoading$.next(false)
        return of(null)
      }),
      map((results) => {
        const responseRows: any[] = results.data || []

        if (responseRows.length === 0) {
          return [{ errorMessage: this.translateService.instant('dialog.pull-pre-auth.no-service-reviews-error') }]
        }

        return responseRows.map((row) => {
          return {
            api: row.api,
            errors: row.errors || [],
          }
        })
      }),
      tap((errors) => {
        this.isLoading$.next(false)
        if (errors.length > 0) {
          this.errorsVisible$.next(true)
        }
      }),
    )

  public resultsVisible$ = combineLatest([
    this.mode$,
    this.serviceReviews$,
  ])
    .pipe(
      map(([mode, serviceReviews]) => mode === 'results' && !!serviceReviews),
    )

  public filteredReferrers$: Observable<ReferrerModel[]>

  public procedureCodes: string[] = []

  public relationshipOptions = Object.values(PatientRelationshipToSubscriber)

  /**
   * Only the Availity request carries a subscriber. The other portals identify the patient from
   * the policy alone, so offering a correction alongside them would silently discard it.
   */
  public get supportsSubscriberOverride(): boolean {
    return this.formGroup.value.authRules?.some((rule) => rule.systemPortal?.api === AuthorizationRuleAPI.AVAILITY) === true
  }

  /** Only worth the space when the policy is held by someone other than the patient. */
  public get showsSubscriber(): boolean {
    return this.formGroup.value.relationship !== PatientRelationshipToSubscriber.SELF
  }

  public get subscriberMissing(): boolean {
    const { subscriberFirstName, subscriberLastName } = this.formGroup.value
    return this.showsSubscriber && !(subscriberFirstName?.trim() && subscriberLastName?.trim())
  }

  public get defaultAuthRuleName(): string {
    if (!this.data.appointment.authRules || this.data.appointment.authRules.length === 0) {
      return ''
    }

    return this.data.appointment.authRules
      .reduce((acc, rule) => {
        if (rule.systemPortal?.isAuthIdentificationSupported && rule.systemPortal?.name && !acc.includes(rule.systemPortal.name)) {
          acc.push(rule.systemPortal.name)
        }
        return acc
      }, [] as string[])
      .join(', ')
  }

  private getPortalKey(rule: AuthorizationRule): string {
    const portalId = rule.systemPortal?.id
    if (portalId) {
      return portalId
    }

    const portalName = rule.systemPortal?.name ?? 'unknown'
    const portalApi = rule.systemPortal?.api ?? 'unknown'
    return `${portalName}::${portalApi}`
  }

  public conflictingFacility$ = combineLatest([
    this.facilityService.current$,
    this.formGroup.get('facility').valueChanges.pipe(
      startWith(this.formGroup.get('facility').value),
    ),
  ])
    .pipe(
      filter(([currentFacility, selectedFacilityId]) => !!currentFacility && !!selectedFacilityId),
      map(([currentFacility, selectedFacilityId]) => currentFacility.id !== selectedFacilityId),
    )

  constructor(
    public dialogRef: MatDialogRef<ServiceReviewInquiryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ServiceReviewInquiryDialogData,
    private readonly patientDataService: PatientDataService,
    private readonly websocketService: WebSocketService,
    private readonly fb: FormBuilder,
    private readonly authorizationService: AppointmentAuthorizationService,
    private readonly crudService: CrudService,
    private readonly snackbar: MatSnackBar,
    private readonly facilityService: FacilityService,
    private readonly insuranceProviderLookupsService: InsuranceProviderLookupsService,
    private readonly translateService: TranslateService,
    private readonly referrersService: ReferrersService,
    private readonly authorizationRulesService: AuthorizationRulesService,
    private readonly clinicService: ClinicService,
  ) { }

  public ngOnInit(): void {
    this.availableServiceTypes = this.data.type === 'referrals'
      ? [InsuranceProviderConfiguration.SUBTYPE_ID.REFERRALS]
      : [InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES, InsuranceProviderConfiguration.SUBTYPE_ID.ADMISSION]
    const defaultServiceReviewType = this.availableServiceTypes[0]

    this.formGroup.patchValue({
      requestTypeCode: defaultServiceReviewType,
      facility: this.data.appointment.locationId,
      orderingPhysicianNPI: this.getDefaultOrderingPhysicianNPI(),
    })

    this.filteredReferrers$ = combineLatest([
      this.referrersService.referrers$,
      this.formGroup.get('orderingPhysicianNPI').valueChanges.pipe(
        startWith(this.formGroup.get('orderingPhysicianNPI').value || ''),
      ),
    ]).pipe(
      map(([referrers, search]) => {
        const term = (search || '').toLowerCase()
        return referrers.filter(
          (r) =>
            r.shortName.toLowerCase().includes(term) ||
            (r.npi ?? '').toLowerCase().includes(term),
        )
      }),
    )

    this.procedureCodes = _filter(uniq(flatten(this.data.appointment.products.map(product => product.codes))))

    if (this.data.insurance) {
      this.formGroup.controls.insurance.setValue(this.data.insurance)
      this.onInsuranceChange()
    } else {
      this.availableInsurances$.pipe(
        takeUntil(this.onDestroy$),
        filter((insuranceOptions) => insuranceOptions.length > 0),
        first(),
      ).subscribe((insuranceOptions) => {
        const pullableOptions = insuranceOptions.filter((option) => !option.disabled)
        const accountId = this.data.appointment?.accountId
        // prefer this visit's own primary insurance, so a shared record or an unrelated policy
        // for another account isn't preselected by array order. Pullable options win, but when
        // no carrier supports 278I they are all disabled and the list is empty, so scope the
        // full list too rather than falling through to insuranceOptions[0] (PIA-2910).
        const preferredInsurance = this.findPreferredInsurance(pullableOptions, accountId)
          ?? this.findPreferredInsurance(insuranceOptions, accountId)
        const insurance = preferredInsurance ?? pullableOptions[0]?.value ?? insuranceOptions[0].value
        this.formGroup.controls.insurance.setValue(insurance)
        this.onInsuranceChange()
      })
    }
  }

  private findPreferredInsurance(
    options: SelectOption<PatientInsurance>[],
    accountId: string | undefined,
  ): PatientInsurance | undefined {
    return findInsuranceForAccount(
      options.map((option) => option.value),
      PatientInsuranceType.PRIMARY,
      this.clinicService.clinic,
      accountId,
    )
  }

  /**
   * Returns the NPI to pre-populate in the ordering physician NPI field,
   * based on the clinic setting `authPullPhysicianRole`.
   * Defaults to the referring physician NPI when the setting is absent.
   * Falls back to the clinic/facility group NPI when no physician NPI is
   * available, mirroring the backend resolveOrderingPhysicianNPI helper.
   * The user can always override the value manually in the dialog.
   */
  private getDefaultOrderingPhysicianNPI(): string | null {
    const role = this.clinicService.clinic?.settings?.authPullPhysicianRole ?? 'referring'
    const appointment = this.data.appointment

    let npi: string | undefined
    if (role === 'attending') {
      npi = appointment.resources?.find((r) => r.id === appointment.attendingPhysicianId)?.npi
    } else if (role === 'referring') {
      npi = appointment.referrer?.npi
    }
    // 'ordering' is free text — no physician NPI; fallback below may still supply a clinic NPI

    if (npi) {
      return npi
    }

    if (role === 'ordering') {
      const orderingResource = appointment.resources?.find(
        (r) => r.id === appointment.orderingPhysicianId,
      )
      if (orderingResource?.npi) {
        return orderingResource?.npi
      }
    }

    const clinic = this.clinicService.clinic
    if (clinic?.provider?.type === 'facility') {
      return appointment.location?.provider?.id ?? null
    }

    // Resource-type clinics have no group NPI — each resource has its own.
    if (clinic?.provider?.type === 'resource') {
      return null
    }
    return clinic?.provider?.id ?? null
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public goBack() {
    this.mode$.next('request')
    this.formGroup.controls.serviceReviews.clearValidators()
    this.formGroup.controls.serviceReviews.updateValueAndValidity()
  }

  public onSubmit(mode: PullPriorAuthorizationMode) {
    if (this.formGroup.invalid) {
      return
    }

    this.errorsVisible$.next(false)

    switch (mode) {
      case 'request':
        if (!this.formGroup.value.authRules?.length) {
          return
        }

        this.isLoading$.next(true)
        this.websocketService.messageQueue.next({
          action: WebsocketAction.PRIOR_AUTHORIZATION_INDEX,
          appointmentId: this.data.appointment.id,
          patientId: this.data.patient.id,
          orderingPhysicianNPI: this.formGroup.value.orderingPhysicianNPI,
          insuranceId: this.formGroup.value.insurance.id,
          providerLookupId: this.formGroup.value.insurance.carrierLookupId,
          facilityId: this.formGroup.value.facility || this.facilityService.current$.getValue()?.id,
          data: this.getPayload(),
        })
        break
      case 'results':
        if (this.formGroup.value.serviceReviews?.length > 0) {
          this.saveSelectedServiceReviews()
        } else {
          this.noSelection = true
        }
        break
    }
  }

  public compareAuthRules(a: AuthorizationRule, b: AuthorizationRule): boolean {
    return a?.id === b?.id
  }

  public onInsuranceChange() {
    const insurance = this.formGroup.get('insurance').value

    this.loadSubscriber(insurance)

    this.authorizationRulesService.collectionParams.next({
      insuranceProviderLookupId: insurance.carrierLookupId,
    })
  }

  /**
   * Most policies belong to the patient, so an unrecorded relationship reads as self and the
   * subscriber fields stay out of the way. A policy already naming a subscriber is the
   * exception: treating it as self would hide the name the pull would otherwise send.
   */
  private loadSubscriber(insurance: PatientInsurance) {
    const hasSubscriberName = !!(insurance.subscriberFirstName || insurance.subscriberLastName)

    this.formGroup.patchValue({
      relationship: insurance.relationship
        ?? (hasSubscriberName ? null : PatientRelationshipToSubscriber.SELF),
      subscriberFirstName: insurance.subscriberFirstName ?? null,
      subscriberLastName: insurance.subscriberLastName ?? null,
    })
  }

  private getPayload(): PullPriorAuthorizationPayload {
    const { requestTypeCode, authRules } = this.formGroup.value
    const inquiryFormValue = (this.formGroup.value.priorAuthorization ?? {}) as ServiceReviewInquiryFormValue

    return {
      requestTypeCode: requestTypeCode ?? InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
      fromDate: inquiryFormValue?.fromDate?.format('YYYY-MM-DD'),
      toDate: inquiryFormValue?.toDate?.format('YYYY-MM-DD'),
      certificationIssueDate: inquiryFormValue?.certificationIssueDate?.format('YYYY-MM-DD'),
      certificationNumber: inquiryFormValue?.certificationNumber,
      referenceNumber: inquiryFormValue?.referenceNumber,
      authRuleIds: authRules?.map((rule) => rule.id) ?? undefined,
      ...this.subscriberOverride(),
    }
  }

  /**
   * A half-entered or blank name would have the payer search for someone who does not exist,
   * so it is dropped here and the pull falls back to the patient, matching the warning shown.
   */
  private subscriberOverride(): Pick<PullPriorAuthorizationPayload, 'relationship' | 'subscriberFirstName' | 'subscriberLastName'> {
    if (!this.supportsSubscriberOverride) {
      return {}
    }

    const { relationship } = this.formGroup.value
    const firstName = this.formGroup.value.subscriberFirstName?.trim()
    const lastName = this.formGroup.value.subscriberLastName?.trim()
    const named = this.showsSubscriber && !!firstName && !!lastName

    return {
      relationship: relationship ?? undefined,
      subscriberFirstName: named ? firstName : undefined,
      subscriberLastName: named ? lastName : undefined,
    }
  }

  private saveSelectedServiceReviews() {
    this.isLoading$.next(true)

    if (this.data.type === 'referrals') {
      this.saveReferrals()
    } else {
      this.saveAuthorizations()
    }
  }

  private saveAuthorizations() {
    const patientInsurance = this.formGroup.value.insurance

    this.authorizationService.bulkCreateAuthorizations(
      this.data.appointment.id,
      patientInsurance.carrierLookupId,
      patientInsurance.id,
      this.formGroup.value.serviceReviews,
    )
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.isLoading$.next(false)),
        catchError((e) => {
          this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })

          return of(null)
        }),
      )
      .subscribe((result) => {
        if (result) {
          if (result.notLinkedCount > 0) {
            this.snackbar.open(
              this.translateService.instant('dialog.pull-pre-auth.not-linked-warning', {
                notLinked: result.notLinkedCount,
                total: result.totalCount,
              }),
              '',
              { duration: 8000 },
            )
          }
          this.dialogRef.close(result.authorizations)
        }
      })
  }

  private saveReferrals() {
    const patientInsurance = this.formGroup.value.insurance
    const serviceReviews = this.formGroup.value.serviceReviews

    const obs = serviceReviews.map(serviceReview => {
      const referral = new ReferralModel({
        patientId: this.data.patient.id,
        appointmentIds: [this.data.appointment.id],
        insuranceId: patientInsurance.id,
        carrierLookupId: patientInsurance.carrierLookupId,
        carrierId: patientInsurance.carrierId,
        number: serviceReview.certificationNumber,
      })
      return this.crudService.create(referral)
    })

    forkJoin(obs).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isLoading$.next(false)),
      catchError((e) => {
        this.snackbar.open(e.error?.message ?? e.message, '', { duration: 5000 })
        return of(null)
      }),
    ).subscribe((referrals) => {
      if (referrals) {
        this.dialogRef.close(referrals)
      }
    })
  }
}
